import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/ui/Button/Button';
import { SidebarFoot } from '@/shared/components/Layout/SidebarFoot/SidebarFoot';
import { SidebarNav } from '@/shared/components/Layout/SidebarNav/SidebarNav';
import {
  selectSidebarCollapsed,
  sidebarToggled,
} from '@/shared/store/reducers/preferences/preferences.slice';
import { useAppDispatch, useAppSelector } from '@/shared/store/typed-hooks/typed-hooks';
import { cn } from '@/shared/utils/cn/cn.util';

/**
 * The PERSISTENT navigation rail (W1) — one of the two hosts of `SidebarNav`, the
 * other being `MobileNav`.
 *
 * `hidden lg:flex` is load-bearing. This is a `shrink-0` in-flow flex child with a
 * fixed width, so on a phone it took 272px of a 375px viewport and left the
 * content ~103px, with no control anywhere that could take it to zero — the
 * collapse toggle only ever went down to a 72px rail. Below `lg` the rail is gone
 * and the drawer is the navigation; from `lg` up the drawer's trigger is hidden
 * and this is.
 *
 * Collapsed state lives in the preferences slice, so it survives a reload — an
 * operator who works on a laptop keeps the rail collapsed and should not have to
 * say so every morning. It is deliberately NOT shared with the drawer: a drawer
 * that opened as a 72px strip of unlabelled icons would be useless.
 *
 * The width comes from `w-sidebar` / `w-sidebar-collapsed` tokens rather than
 * arbitrary pixels (U6), and the panel is bordered on its inline END so the rule
 * sits between it and the content in both directions.
 */
export function Sidebar() {
  const { t } = useTranslation('uiKit');
  const dispatch = useAppDispatch();
  const isCollapsed = useAppSelector(selectSidebarCollapsed);

  return (
    <aside
      aria-label={t('shell.menu')}
      className={cn(
        'hidden shrink-0 flex-col gap-lg border-e border-border bg-card p-md transition-[width] duration-fast ease-standard lg:flex',
        isCollapsed ? 'w-sidebar-collapsed' : 'w-sidebar',
      )}
    >
      <div className={cn('flex items-center', isCollapsed ? 'justify-center' : 'justify-between')}>
        {isCollapsed ? null : (
          <span className="truncate px-sm text-label font-medium text-foreground">
            {t('shell.menu')}
          </span>
        )}
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={isCollapsed ? t('shell.expandSidebar') : t('shell.collapseSidebar')}
          aria-expanded={!isCollapsed}
          onClick={() => {
            dispatch(sidebarToggled());
          }}
        >
          {/* The glyph is mirrored by CSS rather than swapped by hand: the panel
              is on the right in Arabic, so a left-pointing chevron would be
              pointing away from the thing it collapses. */}
          {isCollapsed ? (
            <PanelLeftOpen aria-hidden className="h-4 w-4 rtl:-scale-x-100" />
          ) : (
            <PanelLeftClose aria-hidden className="h-4 w-4 rtl:-scale-x-100" />
          )}
        </Button>
      </div>

      <SidebarNav isCollapsed={isCollapsed} />
      {/* The brand's own rows (P28) sit under the nav; the mark and name are
          the topbar's, which spans this rail, so they are not repeated here. */}
      <SidebarFoot isCollapsed={isCollapsed} />
    </aside>
  );
}
